import { type MouseEvent, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { ThreadListItemPrimitive, useAui, useAuiState } from '@assistant-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';

import { ConfirmDialog } from 'components/Dialog/ConfirmDialog';

import { useAndiAPI } from '../../api/andiClient';
import { UNTITLED_CHAT } from '../../constants';
import { getChatQueryObject } from '../../hooks/useChatQuery';

import classes from './chatListItem.module.css';

/** One chat in the list: a small circle, its name, and rename and delete on hover. assistant-ui marks the open
 *  chat. (Sharing a chat is in the page header.) */
export const ChatListItem = () => {
  const { projectId } = useParams();
  const aui = useAui();
  const queryClient = useQueryClient();
  const getAPI = useAndiAPI();
  const title = useAuiState((state) => state.threadListItem.title);
  const chatId = useAuiState((state) => state.threadListItem.remoteId);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Hovering a chat loads it ahead, so opening it draws at once.
  const prefetch = () => {
    if (chatId) void queryClient.prefetchQuery(getChatQueryObject(projectId, chatId, getAPI));
  };
  const startRename = (event: MouseEvent) => {
    event.stopPropagation();
    setDraft(title ?? '');
    setRenaming(true);
  };
  const finishRename = () => {
    setRenaming(false);
    const name = draft.trim();
    if (name && name !== (title ?? '')) void aui.threadListItem.rename(name);
  };
  const deleteChat = () => {
    setConfirmingDelete(false);
    void aui.threadListItem.delete();
  };

  return (
    <ThreadListItemPrimitive.Root className={classes.item} onMouseEnter={prefetch} onFocus={prefetch}>
      <span className={classes.icon}>
        <span className={classes.dot} />
      </span>
      {renaming ? (
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus -- it opens because the builder asked to rename
          autoFocus
          className={classes.rename}
          value={draft}
          placeholder={title || UNTITLED_CHAT}
          aria-label="Chat name"
          onChange={(event) => setDraft(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') finishRename();
            if (event.key === 'Escape') setRenaming(false);
          }}
          onBlur={finishRename}
        />
      ) : (
        <>
          <ThreadListItemPrimitive.Trigger className={classes.trigger}>
            <span className={classes.title}>
              <ThreadListItemPrimitive.Title fallback={UNTITLED_CHAT} />
            </span>
          </ThreadListItemPrimitive.Trigger>
          <button type="button" className={classes.action} onClick={startRename} aria-label="Rename chat" title="Rename">
            <Pencil size={13} />
          </button>
          <button
            type="button"
            className={classes.action}
            onClick={(event) => {
              event.stopPropagation();
              setConfirmingDelete(true);
            }}
            aria-label="Delete chat"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </>
      )}
      {confirmingDelete && createPortal(
        <ConfirmDialog
          headerText="Delete this chat?"
          bodyText="You can't undo this."
          confirmText="Delete chat"
          variant="danger"
          onConfirm={deleteChat}
          onCancel={() => setConfirmingDelete(false)}
        />,
        document.body,
      )}
    </ThreadListItemPrimitive.Root>
  );
};
